import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { useTransitionStage } from "../api/animalsApi";
import {
  LIFECYCLE_GROUP_LABEL,
  LIFECYCLE_LABEL,
  LIFECYCLE_STATES_BY_GROUP,
} from "../lifecycleStates";
import type { AnimalDetail, LifecycleState } from "../types";
import { getAvailableNextStates } from "../utils/stageTransitions";

const GROUP_ORDER = ["cria", "desarrollo", "productiva", "retirada"] as const;

type Props = {
  accountId: string;
  animal: AnimalDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function ChangeStageDialog({ accountId, animal, open, onOpenChange, onSuccess }: Props) {
  const mutation = useTransitionStage(accountId, animal.id);

  const [occurredAt, setOccurredAt] = useState<Date | undefined>(undefined);
  const [toState, setToState] = useState<LifecycleState | "">("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOccurredAt(new Date());
    setToState("");
    setReason("");
    setError(null);
  }, [open]);

  const fromState = animal.lifecycleState;
  const allowed = useMemo(() => new Set(getAvailableNextStates(fromState)), [fromState]);

  const birthDate = new Date(animal.birthDate);
  const today = new Date();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!occurredAt) {
      setError("Selecciona la fecha del cambio.");
      return;
    }
    if (!toState) {
      setError("Selecciona la etapa destino.");
      return;
    }
    if (toState === fromState) {
      setError("La etapa destino debe ser distinta a la actual.");
      return;
    }
    if (!allowed.has(toState)) {
      setError("Esa transición no es válida.");
      return;
    }

    try {
      await mutation.mutateAsync({
        occurredAt: occurredAt.toISOString(),
        from: fromState,
        to: toState,
        reason: reason.trim() || undefined,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  const pending = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar etapa</DialogTitle>
          <DialogDescription>
            Cambia la etapa del animal de <strong>{LIFECYCLE_LABEL[fromState]}</strong> a otra
            etapa.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="stage-date" className="font-medium text-[13px] text-app-ink-2">
              Fecha del cambio
            </label>
            <DatePicker
              id="stage-date"
              aria-label="Fecha del cambio"
              value={occurredAt}
              onChange={setOccurredAt}
              minDate={birthDate}
              maxDate={today}
              disabled={pending}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="font-medium text-[13px] text-app-ink-2">Etapa actual</span>
            <div
              aria-label="Etapa actual"
              className="rounded-lg border border-app-border bg-app-surface-2 px-3 py-2 text-[14px] text-app-ink-2"
            >
              {LIFECYCLE_LABEL[fromState]}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="stage-to" className="font-medium text-[13px] text-app-ink-2">
              Etapa destino
            </label>
            <Select
              value={toState}
              onValueChange={(v) => setToState(v as LifecycleState)}
              disabled={pending}
            >
              <SelectTrigger
                id="stage-to"
                className="h-10 w-full text-app-ink-1"
                aria-label="Etapa destino"
              >
                <SelectValue placeholder="Selecciona una etapa" />
              </SelectTrigger>
              <SelectContent>
                {GROUP_ORDER.map((group) => {
                  const states = LIFECYCLE_STATES_BY_GROUP[group].filter((s) => allowed.has(s));
                  if (states.length === 0) return null;
                  return (
                    <SelectGroup key={group}>
                      <SelectLabel>{LIFECYCLE_GROUP_LABEL[group]}</SelectLabel>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>
                          {LIFECYCLE_LABEL[state]}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="stage-reason" className="font-medium text-[13px] text-app-ink-2">
              Razón <span className="font-normal text-app-ink-3">(opcional)</span>
            </label>
            <textarea
              id="stage-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              disabled={pending}
              className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
              placeholder="Ej. Reclasificación tras revisión veterinaria"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Cambiando…" : "Cambiar etapa"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
